import { createFileRoute } from "@tanstack/react-router";
import { MapPin, Clock, Smartphone, ShieldCheck, BarChart3, Users } from "lucide-react";
import { SolutionLanding } from "@/components/landing/SolutionLanding";

const URL = "https://busditeads.com/construction-time-tracking-software";
const TITLE = "Construction Time Tracking Software with GPS — Business Dite Ads";
const DESC =
  "GPS time tracking software built for construction crews. Mobile clock-in, geofencing, accurate timesheets and payroll-ready exports. 14-day free trial.";

const faqs = [
  {
    q: "How does GPS clock-in work?",
    a: "Workers open a link on their phone and clock in. Their location is captured at clock-in and clock-out so you know they were on the job site — no app install required.",
  },
  {
    q: "Can I prevent buddy punching?",
    a: "Yes. Each worker has a unique secure token, GPS is captured on every punch, and the system warns when clock-out happens before a normal shift length.",
  },
  {
    q: "Does it export to payroll?",
    a: "Yes. Hours are summarized per employee per pay period and feed directly into payroll runs — supporting hourly and daily pay types.",
  },
  {
    q: "Does it work offline?",
    a: "The crew page works on any phone with a browser. Punches sync as soon as the device is back online.",
  },
];

export const Route = createFileRoute("/construction-time-tracking-software")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "en", href: URL },
      {
        rel: "alternate",
        hrefLang: "pt",
        href: "https://busditeads.com/software-de-controle-de-ponto-para-construcao",
      },
      {
        rel: "alternate",
        hrefLang: "es",
        href: "https://busditeads.com/software-de-control-horario-para-construccion",
      },
      { rel: "alternate", hrefLang: "x-default", href: URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SolutionLanding
      eyebrow="Construction Time Tracking Software"
      h1="GPS time tracking your construction crews will actually use"
      subhead="Mobile clock-in, job-site GPS, accurate timesheets and payroll-ready exports — designed for framers, roofers, concrete and general contractors."
      primaryCtaLabel="Start 14-day free trial"
      secondaryCtaLabel="See pricing"
      benefits={[
        "Works on any smartphone",
        "No app to install",
        "GPS on every punch",
        "Cancel anytime",
      ]}
      featuresHeading="Built for the realities of a job site"
      features={[
        {
          icon: Smartphone,
          title: "Mobile clock-in",
          description:
            "Crews open a secure link and clock in. No installs, no app-store approvals, no IT setup.",
        },
        {
          icon: MapPin,
          title: "GPS verified hours",
          description:
            "Capture location on clock-in and clock-out. Spot punches outside the job site instantly.",
        },
        {
          icon: Clock,
          title: "Accurate timesheets",
          description:
            "Automatic shift calculation, lunch breaks and confirmation prompts to stop accidental punches.",
        },
        {
          icon: BarChart3,
          title: "Labor cost per job",
          description:
            "Hours tie back to projects so you see real labor cost per job site, in real time.",
        },
        {
          icon: Users,
          title: "Crews & roles",
          description: "Add employees and subcontractors, assign roles, and control who sees what.",
        },
        {
          icon: ShieldCheck,
          title: "Audit-ready records",
          description:
            "Immutable punch history, edit log and exports for audits, disputes and compliance.",
        },
      ]}
      howItWorksHeading="From punch to payroll in three steps"
      steps={[
        {
          title: "Send the crew a link",
          description: "Generate a secure clock link per worker — send by SMS, WhatsApp or email.",
        },
        {
          title: "Workers clock with GPS",
          description:
            "Each punch is timestamped and geolocated, tied to the worker and the job site.",
        },
        {
          title: "Approve and pay",
          description:
            "Review timesheets, fix any exceptions, and run payroll with hours pre-loaded.",
        },
      ]}
      socialProofHeading="Why contractors choose our time tracking"
      socialProof={[
        "Cut payroll prep from hours to minutes",
        "Stop paying for hours that weren't worked",
        "Know exactly which crew is on which site",
        "Catch missed clock-outs with smart alerts",
        "Hourly and daily pay rates supported",
        "Available in English, Spanish and Portuguese",
      ]}
      faqHeading="Frequently asked questions"
      faqs={faqs}
      finalCtaHeading="Stop chasing timesheets. Start tracking real hours."
      finalCtaSubhead="Try Business Dite Ads free for 14 days — get your whole crew clocking in on day one."
    />
  );
}
